import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { HelpCircle, X, BookOpen, Mail, Copy, Check, ExternalLink } from 'lucide-react';
import { colorTokens, semanticColors } from '@/common/styles/colorTokens';
import { ArrowIcon, MainMenu, MenuContainer, MenuIcon, MenuItem, MenuLabel, SubMenu, SubMenuItem, SubMenuWrapper } from './components/style';
import NavHeader from './components/NavHeader';
import SpaceNavigation from './components/SpaceNavigation';
import { toastUtils } from '@/common/utils/toastUtils';
import { useNavigationStore } from '@/common/store';
import { NavigationConfig, NavigationMenuItem, NavigationSubMenu, isSubMenu } from './config/types';

interface NavigationProps {
  isOpen: boolean;
  toggleNav: () => void;
  config: NavigationConfig;
  /** SpaceNavigation 아래에 표시될 커스텀 컴포넌트 (펼친 상태) */
  customComponent?: React.ReactNode;
  /** SpaceNavigation 아래에 표시될 커스텀 컴포넌트 (접힌 상태) */
  customComponentCollapsed?: React.ReactNode;
}

// Portal 툴팁 컴포넌트
interface TooltipPortalProps {
  children: React.ReactNode;
  targetRef: React.RefObject<HTMLElement>;
  isVisible: boolean;
}

const TooltipPortal: React.FC<TooltipPortalProps> = ({ children, targetRef, isVisible }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (targetRef.current && isVisible) {
      const rect = targetRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [targetRef, isVisible]);

  if (!isVisible) return null;

  return ReactDOM.createPortal(
    <PortalTooltip style={{ top: position.top, left: position.left }}>
      {children}
    </PortalTooltip>,
    document.body
  );
};

// Portal 서브메뉴 컴포넌트
interface SubMenuPortalProps {
  children: React.ReactNode;
  targetRef: React.RefObject<HTMLElement>;
  isVisible: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const SubMenuPortal: React.FC<SubMenuPortalProps> = ({ children, targetRef, isVisible, onMouseEnter, onMouseLeave }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, bottom: 'auto' as number | 'auto' });
  const [openUpward, setOpenUpward] = useState(false);
  const subMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (targetRef.current && isVisible) {
      const rect = targetRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.top;
      
      // 서브메뉴 예상 높이 (아이템당 약 40px + 헤더 40px)
      const estimatedHeight = 250;
      
      // 하단 공간이 부족하면 위쪽으로 표시
      if (spaceBelow < estimatedHeight && rect.top > estimatedHeight) {
        setOpenUpward(true);
        setPosition({
          top: 0,
          left: rect.right + 8,
          bottom: viewportHeight - rect.bottom,
        });
      } else {
        setOpenUpward(false);
        setPosition({
          top: rect.top,
          left: rect.right + 8,
          bottom: 'auto',
        });
      }
    }
  }, [targetRef, isVisible]);

  if (!isVisible) return null;

  const style = openUpward
    ? { bottom: position.bottom, left: position.left, top: 'auto' }
    : { top: position.top, left: position.left, bottom: 'auto' };

  return ReactDOM.createPortal(
    <PortalSubMenu 
      ref={subMenuRef}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </PortalSubMenu>,
    document.body
  );
};

const Navigation: React.FC<NavigationProps> = ({ isOpen, toggleNav, config, customComponent, customComponentCollapsed }) => {
  const location = useLocation();
  const { isNavCollapsed, toggleNavCollapsed } = useNavigationStore();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Portal 툴팁/서브메뉴 상태
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // hover 처리 함수 (딜레이 적용)
  const handleMouseEnter = useCallback((itemId: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredItem(itemId);
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 150); // 150ms 딜레이
  }, []);

  const supportEmail = config.menuConfig.supportEmail || 'info@factsheet.kr';
  const supportGuideUrl = config.menuConfig.supportGuideUrl;

  // 이메일 복사
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setEmailCopied(true);
      toastUtils.success('이메일이 복사되었습니다.');
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      toastUtils.error('복사에 실패했습니다.');
    }
  };

  // 이메일 앱 열기
  const handleOpenEmailApp = () => {
    window.location.href = `mailto:${supportEmail}?subject=[Factsheet 문의]&body=안녕하세요, Factsheet 사용 중 문의드립니다.%0A%0A`;
  };

  // 사용자 가이드 열기
  const handleOpenGuide = () => {
    window.open(supportGuideUrl, '_blank');
    setShowSupportModal(false);
  };

  // 모달 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowSupportModal(false);
      }
    };
    if (showSupportModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSupportModal]);

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isMenuOpen = (menuId: string) => openMenus.includes(menuId);

  // 권한 체크 함수
  const isMenuDisabled = (menuId: string, menuAuthKey?: string): boolean => {
    // 권한 체크가 비활성화되어 있으면 항상 활성화
    if (config.enableAuthCheck === false) {
      return false;
    }

    if (!menuAuthKey) {
      return false;
    }

    // checkMenuAuth 함수가 없으면 항상 활성화
    if (!config.checkMenuAuth) {
      return false;
    }

    return !config.checkMenuAuth(menuAuthKey);
  };

  // 메뉴 클릭 핸들러
  const handleMenuClick = (item: NavigationMenuItem) => {
    const authKey = item.menuAuthKey || item.id;
    if (isMenuDisabled(item.id, authKey)) {
      toastUtils.error('해당 메뉴에 대한 권한이 없습니다.');
      return;
    }

    // 커스텀 onClick이 있으면 실행
    if (item.onClick) {
      item.onClick();
    }

    // path가 있으면 이동
    // 네비게이션 메뉴 클릭으로 진입했음을 알리는 플래그를 함께 전달한다.
    // (각 페이지에서 새로고침/뒤로가기와 구분하여 상태 초기화 여부를 결정)
    if (item.path) {
      navigate(item.path, { state: { fromNav: true } });
    }

    // config에 onMenuClick이 있으면 실행
    if (config.onMenuClick) {
      config.onMenuClick(item.id);
    }
  };

  // 활성 메뉴 확인
  const isMenuActive = (menuId: string): boolean => {
    const activePath = config.getActivePath(location.pathname);
    return activePath === menuId;
  };

  // 서브메뉴 중 하나라도 활성화되어 있는지 확인
  const isSubMenuGroupActive = (items: NavigationMenuItem[]): boolean => {
    return items.some(item => isMenuActive(item.id));
  };

  // 초기 메뉴 열기 설정
  useEffect(() => {
    config.menuConfig.items.forEach(item => {
      if (isSubMenu(item)) {
        if (isSubMenuGroupActive(item.items)) {
          setOpenMenus(prev => prev.includes(item.id) ? prev : [...prev, item.id]);
        }
      }
    });
  }, [location.pathname]);

  // 일반 메뉴 아이템 렌더링
  const renderMenuItem = (item: NavigationMenuItem) => {
    const IconComponent = item.icon;
    const authKey = item.menuAuthKey || item.id;
    
    return (
      <MenuItem 
        key={item.id}
        onClick={() => handleMenuClick(item)}
        isactive={isMenuActive(item.id)}
        disabled={isMenuDisabled(item.id, authKey)}
      >
        <MenuIcon>
          <IconComponent size={22} />
        </MenuIcon>
        <MenuLabel>{item.label}</MenuLabel>
      </MenuItem>
    );
  };

  // 서브메뉴 그룹 렌더링
  const renderSubMenu = (subMenu: NavigationSubMenu) => {
    const IconComponent = subMenu.icon;
    const isActive = isSubMenuGroupActive(subMenu.items);
    
    return (
      <React.Fragment key={subMenu.id}>
        <MenuItem 
          onClick={() => toggleMenu(subMenu.id)}
          isactive={isActive}
        >
          <MenuIcon>
            <IconComponent size={22} />
          </MenuIcon>
          <MenuLabel>{subMenu.label}</MenuLabel>
          <ArrowIcon>
            {isMenuOpen(subMenu.id) ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
          </ArrowIcon>
        </MenuItem>
        <SubMenuWrapper isopen={isMenuOpen(subMenu.id)} menuCount={subMenu.items.length}>
          <SubMenu>
            {subMenu.items.map(item => {
              const SubIconComponent = item.icon;
              const authKey = item.menuAuthKey || item.id;
              
              return (
                <SubMenuItem
                  key={item.id}
                  onClick={() => handleMenuClick(item)}
                  isactive={isMenuActive(item.id)}
                  disabled={isMenuDisabled(item.id, authKey)}
                >
                  <MenuIcon>
                    <SubIconComponent size={20} />
                  </MenuIcon>
                  <MenuLabel>{item.label}</MenuLabel>
                </SubMenuItem>
              );
            })}
          </SubMenu>
        </SubMenuWrapper>
      </React.Fragment>
    );
  };

  // ref 설정 콜백
  const setItemRef = useCallback((id: string) => (el: HTMLDivElement | null) => {
    itemRefs.current[id] = el;
  }, []);

  // 접힌 메뉴 아이템 렌더링 (Portal 사용)
  const renderCollapsedMenuItem = (item: NavigationMenuItem) => {
    const IconComponent = item.icon;
    const authKey = item.menuAuthKey || item.id;
    const itemId = `menu-${item.id}`;
    
    return (
      <CollapsedMenuItem
        key={item.id}
        ref={setItemRef(itemId)}
        onClick={() => handleMenuClick(item)}
        $isActive={isMenuActive(item.id)}
        $disabled={isMenuDisabled(item.id, authKey)}
        onMouseEnter={() => handleMouseEnter(itemId)}
        onMouseLeave={handleMouseLeave}
      >
        <IconComponent size={22} />
        <TooltipPortal
          targetRef={{ current: itemRefs.current[itemId] } as React.RefObject<HTMLElement>}
          isVisible={hoveredItem === itemId}
        >
          {item.label}
        </TooltipPortal>
      </CollapsedMenuItem>
    );
  };

  // 접힌 서브메뉴 그룹 렌더링 (Portal 사용)
  const renderCollapsedSubMenu = (subMenu: NavigationSubMenu) => {
    const IconComponent = subMenu.icon;
    const isActive = isSubMenuGroupActive(subMenu.items);
    const itemId = `submenu-${subMenu.id}`;
    
    return (
      <CollapsedMenuItemWithSub
        key={subMenu.id}
        ref={setItemRef(itemId)}
        $isActive={isActive}
        onMouseEnter={() => handleMouseEnter(itemId)}
        onMouseLeave={handleMouseLeave}
      >
        <IconComponent size={22} />
        <SubMenuPortal
          targetRef={{ current: itemRefs.current[itemId] } as React.RefObject<HTMLElement>}
          isVisible={hoveredItem === itemId}
          onMouseEnter={() => handleMouseEnter(itemId)}
          onMouseLeave={handleMouseLeave}
        >
          <CollapsedSubMenuHeader>{subMenu.label}</CollapsedSubMenuHeader>
          {subMenu.items.map(item => {
            const SubIconComponent = item.icon;
            const authKey = item.menuAuthKey || item.id;
            
            return (
              <CollapsedSubMenuItem
                key={item.id}
                onClick={() => handleMenuClick(item)}
                $isActive={isMenuActive(item.id)}
                $disabled={isMenuDisabled(item.id, authKey)}
              >
                <SubIconComponent size={18} />
                <span>{item.label}</span>
              </CollapsedSubMenuItem>
            );
          })}
        </SubMenuPortal>
      </CollapsedMenuItemWithSub>
    );
  };

  return (
    <NavContainer $isOpen={isOpen} $isCollapsed={isNavCollapsed}>
      <MenuContainer $isCollapsed={isNavCollapsed}>
        <NavHeader isCollapsed={isNavCollapsed} onToggle={toggleNavCollapsed} />
        <SpaceNavigation isCollapsed={isNavCollapsed} />
        
        {/* 커스텀 컴포넌트 (펼친 상태) */}
        {!isNavCollapsed && customComponent && (
          <CustomComponentWrapper>{customComponent}</CustomComponentWrapper>
        )}
        
        {/* 커스텀 컴포넌트 (접힌 상태) */}
        {isNavCollapsed && customComponentCollapsed && (
          <CustomComponentCollapsedWrapper>{customComponentCollapsed}</CustomComponentCollapsedWrapper>
        )}
        
        {/* 펼친 상태 메뉴 */}
        {!isNavCollapsed && (
          <MainMenu>
            {config.menuConfig.items.map(item => {
              if (isSubMenu(item)) {
                return renderSubMenu(item);
              }
              return renderMenuItem(item);
            })}
          </MainMenu>
        )}
        
        {!isNavCollapsed && (
          <SupportSection>
            <SupportButton onClick={() => setShowSupportModal(true)}>
              <HelpCircle size={18} />
              <span>도움이 필요하신가요?</span>
            </SupportButton>
          </SupportSection>
        )}

        {/* 접힌 상태 메뉴 */}
        {isNavCollapsed && (
          <CollapsedMenu>
            {config.menuConfig.items.map(item => {
              if (isSubMenu(item)) {
                return renderCollapsedSubMenu(item);
              }
              return renderCollapsedMenuItem(item);
            })}

            <CollapsedMenuDivider />

            <CollapsedMenuItem
              ref={setItemRef('help-button')}
              onClick={() => setShowSupportModal(true)}
              onMouseEnter={() => handleMouseEnter('help-button')}
              onMouseLeave={handleMouseLeave}
            >
              <HelpCircle size={20} />
              <TooltipPortal
                targetRef={{ current: itemRefs.current['help-button'] } as React.RefObject<HTMLElement>}
                isVisible={hoveredItem === 'help-button'}
              >
                도움말
              </TooltipPortal>
            </CollapsedMenuItem>
          </CollapsedMenu>
        )}
      </MenuContainer>

      {/* 문의하기 모달 */}
      {showSupportModal && (
        <ModalOverlay>
          <SupportModal ref={modalRef}>
            <ModalHeader>
              <ModalTitle>
                <HelpCircle size={20} />
                고객 지원
              </ModalTitle>
              <ModalCloseButton onClick={() => setShowSupportModal(false)}>
                <X size={18} />
              </ModalCloseButton>
            </ModalHeader>
            
            <ModalContent>
              {supportGuideUrl && (
                <SupportCard onClick={handleOpenGuide}>
                  <SupportCardIcon $variant="guide">
                    <BookOpen size={22} />
                  </SupportCardIcon>
                  <SupportCardContent>
                    <SupportCardTitle>사용자 가이드 보기</SupportCardTitle>
                    <SupportCardDescription>
                      Factsheet 사용법과 기능을 확인해보세요
                    </SupportCardDescription>
                  </SupportCardContent>
                  <ExternalLink size={16} color={colorTokens.gray[400]} />
                </SupportCard>
              )}

              <EmailSupportCard>
                <SupportCardIcon $variant="email">
                  <Mail size={22} />
                </SupportCardIcon>
                <SupportCardContent>
                  <SupportCardTitle>이메일로 문의하기</SupportCardTitle>
                  <SupportCardDescription>
                    담당자가 빠르게 답변드립니다
                  </SupportCardDescription>
                </SupportCardContent>
              </EmailSupportCard>

              <EmailActionArea>
                <EmailAddress>{supportEmail}</EmailAddress>
                <EmailActions>
                  <EmailActionButton onClick={handleCopyEmail}>
                    {emailCopied ? <Check size={14} /> : <Copy size={14} />}
                    {emailCopied ? '복사됨' : '복사'}
                  </EmailActionButton>
                  <EmailActionButton $primary onClick={handleOpenEmailApp}>
                    <Mail size={14} />
                    메일 보내기
                  </EmailActionButton>
                </EmailActions>
              </EmailActionArea>
            </ModalContent>
          </SupportModal>
        </ModalOverlay>
      )}
    </NavContainer>
  );
};

// 스타일 컴포넌트
const CustomComponentWrapper = styled.div`
  padding: 0 1rem;
  margin-bottom: 0.5rem;
`;

const CustomComponentCollapsedWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
`;

const NavContainer = styled.nav<{ $isOpen: boolean; $isCollapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background-color: ${semanticColors.background.secondary};
  border-right: 1px solid ${semanticColors.border.primary};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: visible;
  z-index: 1000;

  width: ${props => !props.$isOpen ? '0' : props.$isCollapsed ? '60px' : '286px'};

  @media (max-width: 768px) {
    width: ${props => props.$isOpen ? '100%' : '0'};
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SupportSection = styled.div`
  padding: 1rem;
  border-top: 1px solid ${semanticColors.border.primary};
  margin-top: auto;
`;

const SupportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  background: ${colorTokens.gray[100]};
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 10px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${colorTokens.gray[800]};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colorTokens.gray[200]};
    color: ${colorTokens.gray[900]};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease;
`;

const SupportModal = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: ${slideUp} 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${colorTokens.gray[200]};
`;

const ModalTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  color: ${colorTokens.gray[800]};
`;

const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: ${colorTokens.gray[500]};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colorTokens.gray[100]};
    color: ${colorTokens.gray[700]};
  }
`;

const ModalContent = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SupportCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem;
  background: ${colorTokens.gray[100]};
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colorTokens.gray[200]};
    border-color: ${colorTokens.gray[300]};
  }
`;

const EmailSupportCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem;
  background: ${colorTokens.brand[100]};
  border: 1px solid ${colorTokens.brand[200]};
  border-radius: 12px 12px 0 0;
`;

const SupportCardIcon = styled.div<{ $variant: 'guide' | 'email' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
  
  ${props => props.$variant === 'guide' && `
    background: ${colorTokens.gray[200]};
    color: ${colorTokens.gray[800]};
  `}
  
  ${props => props.$variant === 'email' && `
    background: ${colorTokens.brand[100]};
    color: ${colorTokens.brand[600]};
  `}
`;

const SupportCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SupportCardTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${colorTokens.gray[800]};
  margin-bottom: 0.125rem;
`;

const SupportCardDescription = styled.div`
  font-size: 0.8125rem;
  color: ${colorTokens.gray[800]};
`;

const EmailActionArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: ${colorTokens.brand[100]};
  border: 1px solid ${colorTokens.brand[200]};
  border-top: none;
  border-radius: 0 0 12px 12px;
`;

const EmailAddress = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${colorTokens.brand[700]};
  text-align: center;
  padding: 0.5rem;
  background: white;
  border-radius: 8px;
  border: 1px dashed ${colorTokens.brand[300]};
`;

const EmailActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EmailActionButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  ${props => props.$primary ? `
    background: ${colorTokens.brand[600]};
    border: none;
    color: white;
    
    &:hover {
      background: ${colorTokens.brand[700]};
    }
  ` : `
    background: white;
    border: 1px solid ${colorTokens.gray[300]};
    color: ${colorTokens.gray[800]};
    
    &:hover {
      background: ${colorTokens.gray[100]};
    }
  `}
`;

// Portal 툴팁 스타일
const PortalTooltip = styled.div`
  position: fixed;
  transform: translateY(-50%);
  padding: 0.5rem 0.75rem;
  background: ${colorTokens.gray[900]};
  color: white;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  z-index: 9999;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: ${colorTokens.gray[900]};
  }
`;

// Portal 서브메뉴 스타일
const PortalSubMenu = styled.div`
  position: fixed;
  min-width: 180px;
  max-height: calc(100vh - 40px);
  background: white;
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  padding: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  animation: ${keyframes`from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); }`} 0.15s ease-out;
  
  /* 스크롤바 스타일 */
  scrollbar-width: thin;
  scrollbar-color: ${colorTokens.gray[300]} transparent;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colorTokens.gray[300]};
    border-radius: 10px;
  }
`;

// 접힌 메뉴 스타일
const CollapsedMenu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  gap: 0.25rem;
  flex: 1;
  overflow-y: auto;
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CollapsedMenuItem = styled.div<{ $isActive?: boolean; $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  color: ${props => props.$isActive ? colorTokens.brand[600] : colorTokens.gray[600]};
  background: ${props => props.$isActive ? colorTokens.brand[100] : 'transparent'};
  opacity: ${props => props.$disabled ? 0.4 : 1};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: ${props => props.$isActive ? colorTokens.brand[100] : colorTokens.gray[100]};
    color: ${props => props.$isActive ? colorTokens.brand[600] : colorTokens.gray[900]};
    transform: translateY(-1px);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

const CollapsedMenuItemWithSub = styled.div<{ $isActive?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  cursor: pointer;
  color: ${props => props.$isActive ? colorTokens.brand[600] : colorTokens.gray[600]};
  background: ${props => props.$isActive ? colorTokens.brand[100] : 'transparent'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: ${props => props.$isActive ? colorTokens.brand[100] : colorTokens.gray[100]};
    color: ${props => props.$isActive ? colorTokens.brand[600] : colorTokens.gray[900]};
    transform: translateY(-1px);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

const CollapsedSubMenuHeader = styled.div`
  padding: 0.75rem 0.75rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${colorTokens.gray[900]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CollapsedSubMenuItem = styled.div<{ $isActive?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$isActive ? colorTokens.brand[600] : colorTokens.gray[800]};
  background: ${props => props.$isActive ? colorTokens.brand[100] : 'transparent'};
  border-radius: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.4 : 1};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$disabled ? 'transparent' : props.$isActive ? colorTokens.brand[100] : colorTokens.gray[100]};
    color: ${props => props.$disabled ? colorTokens.gray[400] : props.$isActive ? colorTokens.brand[700] : colorTokens.gray[900]};
    transform: translateX(2px);
  }
  
  svg {
    flex-shrink: 0;
    color: ${props => props.$isActive ? colorTokens.brand[500] : colorTokens.gray[500]};
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: ${props => props.$isActive ? colorTokens.brand[600] : colorTokens.gray[700]};
  }
  
  span {
    white-space: nowrap;
  }
`;

const CollapsedMenuDivider = styled.div`
  width: 32px;
  height: 1px;
  background: ${colorTokens.gray[200]};
  margin: 0.5rem 0;
`;

export default Navigation;

